import React, { useState, useEffect, useMemo } from 'react';
import { useDashboard } from '../../contexts/DashboardContext';

const MediaModals = () => {
  const {
    API,
    BACKEND_URL,
    axios,
    clients,
    createElement,
    deleteMediaFile,
    fetchAppointmentMedia,
    formatAppointmentDate,
    getElementById,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDragStart,
    handleDrop,
    handleUploadClick,
    isMediaCollapsed,
    isUploading,
    mediaFiles,
    mediaMana,
    newScript,
    openDropboxChooser,
    products,
    saveMediaOrder,
    selectedListingForEdit,
    setAttribute,
    setDragOverIndex,
    setDraggingIndex,
    setIsMediaCollapsed,
    setMediaExpanded,
    setMediaManagerAppointment,
    setShowMediaManager,
    setShowMediaUpload,
    setUploadProgress,
    showMediaManager,
    showMediaUpload,
    showToast,
    uploadProgress,
    uploadSpeed,
    selectedAppointment,
  } = useDashboard();

  // Selected files staged for upload (controlled state so we can render previews)
  const [pendingFiles, setPendingFiles] = useState([]);
  // Generated preview URLs (createObjectURL) for image files — keyed by File reference
  const previewUrls = useMemo(() => {
    const map = new Map();
    pendingFiles.forEach((f) => {
      if (f.type && f.type.startsWith('image/')) {
        map.set(f, URL.createObjectURL(f));
      }
    });
    return map;
  }, [pendingFiles]);

  // Revoke object URLs when the file list changes / modal closes
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Reset staged files whenever the modal is closed
  useEffect(() => {
    if (!showMediaUpload) {
      setPendingFiles([]);
    }
  }, [showMediaUpload]);

  const handleFilesPicked = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setPendingFiles((prev) => [...prev, ...list]);
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  // Smart upload:
  //   - Images < 25 MB → server-side bulk-upload (keeps web-optimized variant)
  //   - Everything else (videos, large images, PDFs) → direct-to-S3 presigned PUT
  //     (bypasses K8s ingress 60s timeout + body-size limits)
  const PRESIGNED_THRESHOLD = 25 * 1024 * 1024;

  const handleStartUpload = async () => {
    if (!pendingFiles.length) {
      showToast?.('Please select files to upload', 'error');
      return;
    }
    const appt = selectedAppointment || selectedListingForEdit;
    if (!appt) {
      showToast?.('Please select an appointment first', 'error');
      return;
    }

    const folder = document.getElementById('folderSelect')?.value || 'general';
    const files = pendingFiles;
    const startTime = Date.now();
    const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
    let totalUploaded = 0;

    setUploadProgress({
      isUploading: true,
      currentFile: '',
      completedFiles: 0,
      totalFiles: files.length,
      overallProgress: 0,
      fileStatuses: files.map((f) => ({ name: f.name, size: f.size, status: 'pending' })),
      uploadSpeed: 0,
      timeRemaining: 0,
    });

    const tickProgress = (fileBytes, fileIdx) => {
      const overall = Math.round(((totalUploaded + fileBytes) / Math.max(totalSize, 1)) * 100);
      const elapsed = (Date.now() - startTime) / 1000;
      const speed = (totalUploaded + fileBytes) / Math.max(elapsed, 0.1);
      const remainingBytes = Math.max(totalSize - (totalUploaded + fileBytes), 0);
      const eta = speed > 0 ? Math.round(remainingBytes / speed) : 0;
      setUploadProgress((prev) => ({
        ...prev,
        currentFile: files[fileIdx]?.name || '',
        overallProgress: overall,
        uploadSpeed: speed / (1024 * 1024),
        timeRemaining: eta,
        fileStatuses: prev.fileStatuses.map((s, i) => (i === fileIdx ? { ...s, status: 'uploading' } : s)),
      }));
    };

    const results = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      tickProgress(0, i);
      try {
        const isImage = (file.type || '').startsWith('image/');
        const usePresigned = !isImage || file.size > PRESIGNED_THRESHOLD;

        if (usePresigned) {
          // Step 1: ask backend for a presigned PUT URL
          const { data: presign } = await axios.post(`${API}/media/presigned-upload-url`, {
            appointment_id: appt.id,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            file_size: file.size,
          });
          // Step 2: PUT bytes directly to S3 (bypasses our K8s ingress).
          //   - Use plain `fetch` so the global axios Authorization header doesn't
          //     get attached (S3 rejects presigned URLs that also carry an
          //     Authorization header).
          //   - No timeout; large bodies stream natively.
          const putResp = await fetch(presign.upload_url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
          });
          if (!putResp.ok) {
            const body = await putResp.text().catch(() => '');
            throw new Error(`S3 ${putResp.status}: ${body.slice(0, 200) || putResp.statusText}`);
          }
          tickProgress(file.size, i);
          // Step 3: register in DB
          const { data: reg } = await axios.post(`${API}/media/register-uploaded-file`, {
            appointment_id: appt.id,
            s3_key: presign.s3_key,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            file_size: file.size,
            folder_name: folder,
            approval_required: true,
          });
          results.push(reg);
        } else {
          // Small images go through bulk-upload so the server can build a web variant
          const fd = new FormData();
          fd.append('files', file);
          fd.append('appointment_id', appt.id);
          fd.append('folder_name', folder);
          fd.append('approval_required', 'true');
          const { data } = await axios.post(`${API}/media/bulk-upload`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (evt) => {
              if (evt.total) tickProgress(evt.loaded, i);
            },
          });
          results.push(...(Array.isArray(data) ? data : []));
        }

        totalUploaded += file.size;
        setUploadProgress((prev) => ({
          ...prev,
          completedFiles: i + 1,
          fileStatuses: prev.fileStatuses.map((s, idx) => (idx === i ? { ...s, status: 'completed' } : s)),
        }));
      } catch (err) {
        const status = err.response?.status;
        const detail =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'unknown error';
        const msg = status ? `HTTP ${status}: ${detail}` : detail;
        console.error(`Upload failed for ${file.name}:`, status, detail, err);
        results.push({ success: false, filename: file.name, message: msg });
        setUploadProgress((prev) => ({
          ...prev,
          completedFiles: i + 1,
          fileStatuses: prev.fileStatuses.map((s, idx) =>
            idx === i ? { ...s, status: 'failed', error: msg } : s,
          ),
        }));
      }
    }

    const successful = results.filter((r) => r.success).length;
    const failed = results.length - successful;
    const failedFiles = results.filter((r) => !r.success);

    if (failed === 0) {
      showToast?.(`Uploaded ${successful} file${successful === 1 ? '' : 's'}`, 'success');
    } else {
      const sample = failedFiles
        .slice(0, 3)
        .map((r) => `${r.filename}: ${r.message}`)
        .join('\n');
      showToast?.(
        `Uploaded ${successful}, failed ${failed}.\n${sample}${failedFiles.length > 3 ? '\n…' : ''}`,
        failed === results.length ? 'error' : 'warning',
      );
    }

    setUploadProgress({
      isUploading: false,
      currentFile: '',
      completedFiles: 0,
      totalFiles: 0,
      overallProgress: 0,
      fileStatuses: [],
      uploadSpeed: 0,
      timeRemaining: 0,
    });
    setPendingFiles([]);
    setShowMediaUpload(false);
    if (appt?.id) fetchAppointmentMedia?.(appt.id);
  };


  return (
    <>
            {/* Upload Modal with Progress - Clara Labs Style */}
            {showMediaUpload && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[150]" onClick={(e) => { if (e.target === e.currentTarget && !uploadProgress.isUploading) setShowMediaUpload(false); }}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {uploadProgress.isUploading ? 'Uploading…' : 'Upload Media'}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {uploadProgress.isUploading ? 'Please keep this window open' : 'Drop files or import from Dropbox'}
                      </p>
                    </div>
                    {!uploadProgress.isUploading && (
                      <button
                        type="button"
                        onClick={() => setShowMediaUpload(false)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                        data-testid="close-upload-modal"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {!uploadProgress.isUploading ? (
                    <>
                      {/* Upload Form */}
                      <div className="p-6 space-y-5">
                        {/* Big Drop Zone */}
                        <label
                          htmlFor="fileInput"
                          className="group relative flex flex-col items-center justify-center w-full h-44 rounded-xl border-2 border-dashed border-gray-300 hover:border-indigo-500 hover:bg-indigo-50/40 transition-all cursor-pointer"
                          data-testid="upload-dropzone"
                        >
                          <div className="w-14 h-14 rounded-full bg-gray-100 group-hover:bg-indigo-100 flex items-center justify-center mb-3 transition-colors">
                            <svg className="w-7 h-7 text-gray-500 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            <span className="text-indigo-600">Click to browse</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">JPG, PNG, MP4, MOV, PDF • Up to 200MB each</p>
                          <input
                            id="fileInput"
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.pdf,.doc,.docx"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={handleFilesPicked}
                            data-testid="upload-file-input"
                          />
                        </label>

                        {/* Selected files preview */}
                        {pendingFiles.length > 0 && (
                          <div className="border border-gray-200 rounded-xl bg-gray-50/40 overflow-hidden" data-testid="pending-files-list">
                            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                {pendingFiles.length} file{pendingFiles.length === 1 ? '' : 's'} selected
                                <span className="ml-2 text-gray-400 normal-case font-normal">
                                  ({formatBytes(pendingFiles.reduce((s, f) => s + f.size, 0))} total)
                                </span>
                              </span>
                              <button
                                type="button"
                                onClick={() => setPendingFiles([])}
                                className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                                data-testid="clear-pending-files-btn"
                              >
                                Clear all
                              </button>
                            </div>
                            <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                              {pendingFiles.map((f, i) => {
                                const url = previewUrls.get(f);
                                const isImage = (f.type || '').startsWith('image/');
                                const isVideo = (f.type || '').startsWith('video/');
                                return (
                                  <li
                                    key={`${f.name}-${i}`}
                                    className="flex items-center gap-3 px-4 py-2 hover:bg-white transition-colors"
                                    data-testid={`pending-file-row-${i}`}
                                  >
                                    {url ? (
                                      <img
                                        src={url}
                                        alt={f.name}
                                        className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-200"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          {isVideo ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                          ) : isImage ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          )}
                                        </svg>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate" title={f.name}>{f.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {formatBytes(f.size)}
                                        {isVideo && <span className="ml-2 text-indigo-600">• direct upload</span>}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removePendingFile(i)}
                                      className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                      title="Remove"
                                      data-testid={`remove-pending-file-${i}`}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* Divider */}
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                          <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400 uppercase tracking-wider">or</span></div>
                        </div>

                        {/* Dropbox Import Button */}
                        <button
                          type="button"
                          data-testid="dropbox-import-btn"
                          onClick={async () => {
                                  try {
                                    console.log('Getting Dropbox App Key...');
                                    // Get Dropbox App Key from backend
                                    const keyResponse = await axios.get(`${API}/integrations/dropbox/app-key`);
                                    const appKey = keyResponse.data.app_key;
                                    console.log('App Key received:', appKey ? appKey.substring(0, 10) + '...' : 'null');
                                    
                                    if (!appKey) {
                                      showToast('Dropbox not configured. Go to Settings → Integrations to set up your Dropbox App Key.', 'error');
                                      return;
                                    }
                                    
                                    // Check if it's a placeholder/test key
                                    if (appKey.includes('test') || appKey.length < 10) {
                                      showToast('Invalid Dropbox App Key. Please update it in Settings → Integrations.', 'error');
                                      return;
                                    }
                                    
                                    console.log('Loading Dropbox Chooser SDK...');
                                    
                                    // Check if Dropbox SDK is already loaded with the same key
                                    const existingScript = document.getElementById('dropboxjs');
                                    if (existingScript && window.Dropbox && window.Dropbox.choose) {
                                      console.log('Using existing Dropbox SDK');
                                      openDropboxChooser(appKey);
                                      return;
                                    }
                                    
                                    // Remove any existing Dropbox scripts
                                    if (existingScript) {
                                      existingScript.remove();
                                      delete window.Dropbox;
                                    }
                                    
                                    // Define chooser opener function
                                    const openDropboxChooser = () => {
                                      if (!window.Dropbox || !window.Dropbox.choose) {
                                        showToast('Dropbox Chooser failed to initialize. Please refresh the page and try again.', 'error');
                                        return;
                                      }
                                      
                                      window.Dropbox.choose({
                                        success: async (files) => {
                                          console.log('Files selected:', files.length, 'files');
                                          
                                          if (files.length === 0) {
                                            showToast('No files selected', 'info');
                                            return;
                                          }
                                          
                                          // Initialize upload progress with proper fileStatuses array
                                          setUploadProgress({
                                            isUploading: true,
                                            progress: 0,
                                            currentFile: `Importing ${files.length} file(s) from Dropbox...`,
                                            totalFiles: files.length,
                                            uploadedFiles: 0,
                                            fileStatuses: files.map((f, idx) => ({
                                              name: f.name,
                                              status: 'pending',
                                              index: idx
                                            }))
                                          });
                                          
                                          try {
                                            const folder = document.getElementById('folderSelect')?.value || 'general';
                                            
                                            // Update progress
                                            setUploadProgress(prev => ({
                                              ...prev,
                                              progress: 10,
                                              currentFile: `Importing ${files.length} file(s) from Dropbox...`
                                            }));
                                            
                                            const response = await axios.post(`${API}/integrations/dropbox/import-from-chooser`, {
                                              files: files,
                                              appointment_id: selectedListingForEdit.id,
                                              folder_name: folder
                                            }, {
                                              timeout: 300000 // 5 minute timeout for imports
                                            });
                                              
                                            console.log('Import response:', response.data);
                                            
                                            // Show completion progress
                                            setUploadProgress(prev => ({
                                              ...prev,
                                              progress: 90,
                                              currentFile: 'Processing complete. Loading images...',
                                              uploadedFiles: files.length,
                                              fileStatuses: prev.fileStatuses.map(s => ({...s, status: 'success'}))
                                            }));
                                            
                                            // Refresh the media list immediately
                                            console.log('Refreshing media list...');
                                            await fetchAppointmentMedia(selectedListingForEdit.id);
                                            console.log('Media list refreshed');
                                            
                                            // Expand Images section if not already expanded
                                            setMediaExpanded(prev => ({...prev, images: true}));
                                            
                                            // Reset upload progress
                                            setUploadProgress({
                                              isUploading: false,
                                              progress: 0,
                                              currentFile: '',
                                              totalFiles: 0,
                                              uploadedFiles: 0,
                                              fileStatuses: []
                                            });
                                            
                                            setShowMediaUpload(false);
                                            
                                            // Show detailed results
                                            const importedCount = response?.data?.imported_count || 0;
                                            const failedCount = response?.data?.failed_count || 0;
                                            
                                            if (failedCount > 0 && response?.data?.files) {
                                              const failedFiles = response.data.files.filter(f => f.status === 'failed');
                                              const failedList = failedFiles.map(f => `${f.filename}: ${f.error || 'Unknown error'}`).join(', ');
                                              showToast(`Imported ${importedCount} file(s). Failed: ${failedCount} (${failedList})`, failedCount > importedCount ? 'error' : 'warning');
                                            } else if (importedCount > 0) {
                                              showToast(`Successfully imported ${importedCount} file(s) from Dropbox!`, 'success');
                                            } else {
                                              showToast('No files were imported. Please try again.', 'error');
                                            }
                                          } catch (error) {
                                            console.error('Import error:', error);
                                            setUploadProgress({
                                              isUploading: false,
                                              progress: 0,
                                              currentFile: '',
                                              totalFiles: 0,
                                              uploadedFiles: 0,
                                              fileStatuses: []
                                            });
                                            showToast('Failed to import from Dropbox: ' + (error.response?.data?.detail || error.message), 'error');
                                          }
                                        },
                                        cancel: () => {
                                          console.log('User cancelled Dropbox chooser');
                                        },
                                        linkType: "direct",
                                        multiselect: true,
                                        extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.mp4', '.mov', '.avi'],
                                      });
                                    };
                                    
                                    // Load fresh Dropbox SDK with the correct app key
                                    const newScript = document.createElement('script');
                                    newScript.id = 'dropboxjs';
                                    newScript.type = 'text/javascript';
                                    newScript.src = 'https://www.dropbox.com/static/api/2/dropins.js';
                                    newScript.setAttribute('data-app-key', appKey);
                                    
                                    newScript.onload = () => {
                                      console.log('Dropbox SDK loaded, opening chooser...');
                                      // Small delay to ensure SDK is fully initialized
                                      setTimeout(() => {
                                        openDropboxChooser();
                                      }, 100);
                                    };
                                    
                                    newScript.onerror = () => {
                                      console.error('Failed to load Dropbox SDK');
                                      showToast('Failed to load Dropbox. Please check your internet connection.', 'error');
                                    };
                                    
                                    document.head.appendChild(newScript);
                                    
                                  } catch (error) {
                                    console.error('Dropbox setup error:', error);
                                    showToast('Dropbox error: ' + (error.response?.data?.detail || error.message), 'error');
                                  }
                                }}
                          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-white border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50/30 text-gray-700 hover:text-blue-700 rounded-xl transition-all font-medium text-sm"
                        >
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452 0 13.274zm12 0l6.001 3.822 6-3.822-6-3.822-6.001 3.822zM6 17.097l6.001 3.806 6-3.806L12 13.274 6 17.097z"/>
                          </svg>
                          Import from Dropbox
                        </button>

                        {/* Folder Selector */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Save to folder</label>
                          <select
                            id="folderSelect"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                            defaultValue="general"
                          >
                            <option value="general">General</option>
                            <option value="photos">Photos</option>
                            <option value="videos">Videos</option>
                            <option value="documents">Documents</option>
                          </select>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowMediaUpload(false)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                          data-testid="cancel-upload-btn"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleStartUpload}
                          disabled={pendingFiles.length === 0}
                          className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
                          data-testid="confirm-upload-btn"
                        >
                          {pendingFiles.length > 0 ? `Upload ${pendingFiles.length} file${pendingFiles.length === 1 ? '' : 's'}` : 'Upload Files'}
                        </button>
                      </div>
                    </>
                  ) : (
                    /* Progress Display */
                    <div className="p-6">
                      <div className="space-y-4">
                        {/* Overall Progress */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium text-gray-900">
                              Uploading ({uploadProgress.completedFiles} of {uploadProgress.totalFiles} files)
                            </span>
                            <span className="text-gray-600">{uploadProgress.overallProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${uploadProgress.overallProgress}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Current File */}
                        {uploadProgress.currentFile && (
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-sm font-medium text-blue-900 mb-1">Currently uploading:</p>
                            <p className="text-sm text-blue-700 truncate">{uploadProgress.currentFile}</p>
                          </div>
                        )}

                        {/* Upload Stats */}
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>
                            Speed: {uploadProgress.uploadSpeed > 0 ? `${uploadProgress.uploadSpeed.toFixed(1)} MB/s` : 'Calculating...'}
                          </span>
                          <span>
                            {uploadProgress.timeRemaining > 0 ? 
                              `ETA: ${Math.floor(uploadProgress.timeRemaining / 60)}:${(uploadProgress.timeRemaining % 60).toString().padStart(2, '0')}` : 
                              'Almost done...'
                            }
                          </span>
                        </div>

                        {/* File Status List */}
                        <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-3">
                          {(uploadProgress.fileStatuses || []).map((file, index) => (
                            <div key={index} className="flex items-center justify-between py-2">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  {file.status === 'completed' && <span className="text-green-500">✅</span>}
                                  {file.status === 'uploading' && <span className="text-blue-500 animate-pulse">⏳</span>}
                                  {file.status === 'failed' && <span className="text-red-500">❌</span>}
                                  {file.status === 'pending' && <span className="text-gray-400">⏸️</span>}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                  <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
        {/* Media Manager Modal */}
        {showMediaManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[150] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] md:max-w-6xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Listing Details</h2>
                    {mediaManagerAppointment && (
                      <p className="text-gray-600 mt-1">
                        {mediaManagerAppointment.property_address}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowMediaManager(false);
                      setMediaManagerAppointment(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Order Details Section */}
                {mediaManagerAppointment && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Information</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Order #:</span>
                            <span className="text-gray-900 font-medium text-sm">#5501</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Service:</span>
                            <span className="text-gray-900 font-medium text-sm">{mediaManagerAppointment.service_type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Total Price:</span>
                            <span className="text-gray-900 font-medium text-sm">${mediaManagerAppointment.total_price || '185.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Items:</span>
                            <span className="text-gray-900 font-medium text-sm">({mediaManagerAppointment.selected_products?.length || 1})</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Client & Schedule</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Client:</span>
                            <span className="text-gray-900 font-medium text-sm">{mediaManagerAppointment.client_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Email:</span>
                            <span className="text-gray-900 font-medium text-sm">{clients.find(c => c.id === mediaManagerAppointment.client_id)?.email || 'client@example.com'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Date & Time:</span>
                            <span className="text-gray-900 font-medium text-sm">{formatAppointmentDate(mediaManagerAppointment.scheduled_date, mediaManagerAppointment.scheduled_time)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 text-sm">Status:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              mediaManagerAppointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              mediaManagerAppointment.status === 'in_progress' ? 'bg-blue-100 text-blue-300' :
                              mediaManagerAppointment.status === 'scheduled' ? 'bg-yellow-100 text-yellow-300' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {mediaManagerAppointment.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Media Files Collapsible Section */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  {/* Collapsible Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMediaCollapsed(!isMediaCollapsed)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-gray-400">
                        <svg 
                          className={`w-5 h-5 transition-transform duration-200 ${isMediaCollapsed ? '' : 'rotate-90'}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Media Files ({mediaFiles.filter(file => file && file.id).length})
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMediaUpload(true);
                        }}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 font-medium"
                      >
                        Add Media
                      </button>
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {!isMediaCollapsed && (
                    <div className="border-t border-gray-200">
                      {mediaFiles.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="text-6xl mb-4">📷</div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Media Files Yet</h3>
                          <p className="text-gray-600 mb-4">Upload some photos and videos to get started</p>
                          <button
                            onClick={() => setShowMediaUpload(true)}
                            className="clara-btn-primary px-6 py-3"
                          >
                            Upload Media
                          </button>
                        </div>
                      ) : (
                        <div className="p-4">
                          <div className="flex justify-between items-center mb-6">
                            <p className="text-gray-600">Drag and drop to reorder photos (Click and hold to drag)</p>
                            <button
                              onClick={saveMediaOrder}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
                            >
                              Save Order
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {safeMediaFiles.filter(file => file && file.id).map((file, index) => (
                              <div
                                key={file.id}
                                draggable="true"
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragEnter={(e) => handleDragEnter(e, index)}
                                onDragLeave={(e) => handleDragLeave(e)}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={() => {
                                  console.log('Drag ended');
                                  setDraggingIndex(null);
                                  setDragOverIndex(null);
                                }}
                                className={`bg-white border-2 rounded-lg overflow-hidden cursor-move hover:shadow-lg transition-all duration-200 ${
                                  draggingIndex === index 
                                    ? 'opacity-50 border-blue-500 transform scale-105' 
                                    : dragOverIndex === index 
                                      ? 'border-green-500 bg-green-50 transform scale-105' 
                                      : 'border-gray-200'
                                }`}
                              >
                                <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                                  <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                    #{index + 1}
                                  </div>
                                  {file.mime_type && file.mime_type.startsWith('image/') ? (
                                    <img 
                                      src={file.thumbnailUrl || `${BACKEND_URL}/api/media/${file.id}`}
                                      alt={file.original_filename || 'Media file'}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : file.mime_type && file.mime_type.startsWith('video/') ? (
                                    <div className="text-6xl text-gray-400">🎥</div>
                                  ) : (
                                    <div className="text-6xl text-gray-400">📄</div>
                                  )}
                                  {/* Fallback */}
                                  <div className="hidden w-full h-full flex-col items-center justify-center text-gray-400">
                                    <div className="text-4xl mb-2">❌</div>
                                    <p className="text-xs">Preview unavailable</p>
                                  </div>
                                </div>
                                <div className="p-3">
                                  <p className="font-medium text-sm text-gray-900 truncate mb-1" title={file.original_filename || 'Unknown file'}>
                                    {file.original_filename || 'Unknown file'}
                                  </p>
                                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                                    <span>{file.file_size ? (file.file_size / (1024 * 1024)).toFixed(1) : '0'} MB</span>
                                    <span className="bg-gray-100 px-2 py-1 rounded">{file.folder_name || 'Unknown'}</span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between mt-2">
                                    <button
                                      onClick={() => {
                                        if (file.id) {
                                          const link = document.createElement('a');
                                          link.href = `${BACKEND_URL}/api/media/${file.id}`;
                                          link.download = file.original_filename || 'download';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }
                                      }}
                                      className="text-blue-400 hover:text-blue-700 text-xs font-medium"
                                    >
                                      Download
                                    </button>
                                    <button
                                      onClick={() => file.id && deleteMediaFile(file.id)}
                                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default MediaModals;
